import { useRef, useEffect, useCallback, useState } from 'react';
import io from 'socket.io-client';
import { SIGNAL_SERVER_URL, SOCKET_OPTIONS, getHlsUrl } from '../utils/constants';
import { notifyUnauthorized } from '../utils/auth';

export function useSocket({
  accessGranted,
  viewerId,
  mediaSoup,
  setConnectionStatus,
  setActiveUnits,
  selectedUnit,
  setSelectedUnit,
  socketRef, // Accept external socket ref
}) {
  const internalSocketRef = useRef(null);
  const socket = socketRef ?? internalSocketRef;
  // The ref alone can't tell other hooks that a socket now exists; this state can
  const [socketInstance, setSocketInstance] = useState(null);

  // Use refs so socket listeners always call the LATEST version of callbacks
  // without needing to re-register listeners when deps change
  const mediaSoupRef = useRef(mediaSoup);
  const selectedUnitRef = useRef(selectedUnit);
  mediaSoupRef.current = mediaSoup;
  selectedUnitRef.current = selectedUnit;

  const handleAvailableUnits = useCallback(async (units) => {
    const ms = mediaSoupRef.current;
    console.log(`📋 Available units:`, units.length);

    for (const unit of units) {
      const hlsUrl = getHlsUrl(unit.metadata);

      setActiveUnits(prev => {
        const newMap = new Map(prev);
        newMap.set(unit.unitId, {
          unitId: unit.unitId,
          metadata: unit.metadata,
          isOnline: true,
          lastUpdate: Date.now(),
        });
        return newMap;
      });

      if (unit.videoProducerId) {
        ms.storeProducer(unit.unitId, 'video', unit.videoProducerId);
      }
      if (unit.audioProducerId) {
        ms.storeProducer(unit.unitId, 'audio', unit.audioProducerId);
      }

      if (!hlsUrl) {
        if (unit.videoProducerId) {
          try {
            await ms.consumeMedia(unit.unitId, 'video', unit.videoProducerId);
          } catch (error) {
            console.error(`Failed to consume video for ${unit.unitId}:`, error);
          }
        }
        if (unit.audioProducerId) {
          try {
            await ms.consumeMedia(unit.unitId, 'audio', unit.audioProducerId);
          } catch (error) {
            console.error(`Failed to consume audio for ${unit.unitId}:`, error);
          }
        }
      }
    }
  }, [setActiveUnits]);

  const handleNewProducer = useCallback(async ({ producerId, unitId, kind, metadata }) => {
    const ms = mediaSoupRef.current;
    console.log(`📹 New producer from ${unitId}: ${kind} (ID: ${producerId})`);

    if (getHlsUrl(metadata)) {
      console.log(`⏭️ HLS unit ${unitId} uses external playback - skip consumer churn`);
      return;
    }

    const existingProducerId = ms.getProducerId(unitId, kind);
    if (existingProducerId === producerId) {
      console.log(`⏭️ Skipping - same ${kind} producer ${producerId} for ${unitId}`);
      return;
    }

    console.log(`🔄 Replacing ${kind} producer for ${unitId}: ${existingProducerId} → ${producerId}`);
    ms.closeConsumer(unitId, kind);
    ms.storeProducer(unitId, kind, producerId);

    setActiveUnits(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(unitId) || {};
      existing.metadata = metadata || existing.metadata;
      existing.lastUpdate = Date.now();
      existing.isOnline = true;
      newMap.set(unitId, existing);
      return newMap;
    });

    try {
      await ms.consumeMedia(unitId, kind, producerId);
      console.log(`✅ Successfully consumed new ${kind} for ${unitId}`);
    } catch (error) {
      console.error(`❌ Failed to consume ${kind} for ${unitId}:`, error);
    }
  }, [setActiveUnits]);

  useEffect(() => {
    if (!accessGranted) {
      setConnectionStatus('Awaiting Access');
      return;
    }

    console.log('🚀 Initializing MediaSoup viewer...');
    setConnectionStatus('Connecting...');

    const s = io(SIGNAL_SERVER_URL, SOCKET_OPTIONS);
    socket.current = s;
    setSocketInstance(s);
    let disposed = false;

    // --- Named handlers so we can remove them in cleanup ---
    const onConnect = () => {
      console.log('✅ Connected to server');
      setConnectionStatus('Connected');
      // Every (re)connection starts from a clean mediasoup state before routerCapabilities arrives
      mediaSoupRef.current.resetDevice();
    };

    const onConnectError = (error) => {
      console.error('❌ Connection error:', error.message);
      setConnectionStatus('Connection Failed');
    };

    const onError = (err) => {
      console.error('❌ Socket error:', err);
    };

    const onDisconnect = (reason) => {
      console.log('📴 Disconnected:', reason);
      setConnectionStatus('Disconnected');
      mediaSoupRef.current.resetDevice();
      mediaSoupRef.current.stopAllHealthChecks();
      // socket.io does not auto-reconnect after a server-side kick (e.g. mediasoup worker restart).
      if (reason === 'io server disconnect') {
        setTimeout(() => { if (!disposed && !s.connected) s.connect(); }, 1000);
      }
    };

    const onRouterCapabilities = async (routerRtpCapabilities) => {
      try {
        console.log('📥 Received router capabilities');
        setConnectionStatus('Initializing device...');

        await mediaSoupRef.current.initDevice(routerRtpCapabilities);
        await mediaSoupRef.current.flushQueuedConsumptions();

        setConnectionStatus('Ready');

        s.emit('viewer-join', {
          viewerId: viewerId.current,
          metadata: {
            name: 'Viewer ' + viewerId.current.slice(-4),
            userAgent: navigator.userAgent,
          }
        });
      } catch (error) {
        console.error('❌ Error loading device:', error);
        setConnectionStatus('Error');
      }
    };

    const onUnitAvailable = async ({ unitId, metadata }) => {
      console.log(`🚑 New unit available: ${unitId}`);
      setActiveUnits(prev => {
        const newMap = new Map(prev);
        newMap.set(unitId, {
          unitId,
          metadata,
          isOnline: true,
          lastUpdate: Date.now(),
        });
        return newMap;
      });
    };

    const onUnitDisconnected = ({ unitId }) => {
      console.log(`📴 Unit disconnected: ${unitId}`);
      mediaSoupRef.current.cleanupUnit(unitId);

      setActiveUnits(prev => {
        const newMap = new Map(prev);
        newMap.delete(unitId);
        return newMap;
      });

      if (selectedUnitRef.current === unitId) {
        setSelectedUnit(null);
      }
    };

    const onProducerClosed = ({ producerId, kind, metadata }) => {
      const unitId = metadata?.unitId;
      if (unitId) {
        console.log(`📴 Producer closed: ${unitId} ${kind}`);
        mediaSoupRef.current.removeProducer(unitId, kind);
        mediaSoupRef.current.closeConsumer(unitId, kind);
      }
    };

    const onMetadataUpdated = ({ unitId, metadata }) => {
      setActiveUnits(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(unitId);
        if (existing) {
          existing.metadata = metadata;
          existing.lastUpdate = Date.now();
          newMap.set(unitId, existing);
        }
        return newMap;
      });
    };

    // The server refused an operator action or the viewer join: the session cookie is gone
    const onAuthRequired = ({ event } = {}) => {
      console.warn(`🔒 Server requires a new session (${event || 'unknown event'})`);
      notifyUnauthorized();
    };

    // Register all listeners
    s.on('auth-required', onAuthRequired);
    s.on('connect', onConnect);
    s.on('connect_error', onConnectError);
    s.on('error', onError);
    s.on('disconnect', onDisconnect);
    s.on('routerCapabilities', onRouterCapabilities);
    s.on('available-units', handleAvailableUnits);
    s.on('unit-available', onUnitAvailable);
    s.on('new-producer', handleNewProducer);
    s.on('unit-disconnected', onUnitDisconnected);
    s.on('producer-closed', onProducerClosed);
    s.on('metadata-updated', onMetadataUpdated);

    // Cleanup (logout / loss of access): remove ALL listeners then disconnect
    return () => {
      disposed = true;
      mediaSoupRef.current.stopAllHealthChecks();
      // The 'disconnect' listener is removed below, so reset here: the next login must not
      // reuse this socket's recv transport, consumers or producer list
      mediaSoupRef.current.resetDevice(true);
      setActiveUnits(new Map());

      s.off('auth-required', onAuthRequired);
      s.off('connect', onConnect);
      s.off('connect_error', onConnectError);
      s.off('error', onError);
      s.off('disconnect', onDisconnect);
      s.off('routerCapabilities', onRouterCapabilities);
      s.off('available-units', handleAvailableUnits);
      s.off('unit-available', onUnitAvailable);
      s.off('new-producer', handleNewProducer);
      s.off('unit-disconnected', onUnitDisconnected);
      s.off('producer-closed', onProducerClosed);
      s.off('metadata-updated', onMetadataUpdated);

      s.disconnect();
      if (socket.current === s) socket.current = null;
      setSocketInstance(null);
    };
  }, [accessGranted, handleAvailableUnits, handleNewProducer, setConnectionStatus, setActiveUnits, setSelectedUnit, viewerId, socket]);

  return { socketRef: socket, socketInstance };
}
